import React, { useEffect, useState } from "react";
import { api } from "../scripts/api";

const TABLE_OPTIONS = [
  { value: "all_inventory", label: "All Inventory" },
  { value: "stock_inventory", label: "Stock Inventory" },
  { value: "used_inventory", label: "Used Inventory" },
  { value: "sold_inventory", label: "Sold Inventory" },
];

const DEDUPE_OPTIONS = [
  { value: "vin", label: "VIN" },
  { value: "rowo", label: "RO/WO" },
  { value: "stock_po", label: "Stock PO" },
];

export default function InventoryImportPage() {
  const [file, setFile] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheet, setSheet] = useState("");
  const [headerRow, setHeaderRow] = useState(3);
  const [table, setTable] = useState("all_inventory");
  const [dedupe, setDedupe] = useState("vin");
  const [headers, setHeaders] = useState([]);
  const [targetCols, setTargetCols] = useState([]);
  const [mapping, setMapping] = useState({});
  const [sample, setSample] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [replaceAll, setReplaceAll] = useState(false);
  const [mergeComponents, setMergeComponents] = useState(false);
  const targets = [...new Set((targetCols && targetCols.length ? targetCols : Object.keys(mapping).concat(headers)))];

  const handlePreview = async () => {
    if (!file) return setMsg("Choose a file first.");
    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sheet", sheet);
      fd.append("header_row", headerRow);
      fd.append("table", table);
      const { data } = await api.post("/import/inventory/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSheetNames(data.sheetNames || []);
      if (!sheet) setSheet(data.sheet);
      setHeaders(data.headers || []);
      setTargetCols(data.columns || []);
      setMapping(data.suggestedMapping || {});
      setSample(data.sample || []);
      setRowCount(data.rowCount || 0);
      setMsg(`Preview loaded: ${data.rowCount || 0} rows`);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Preview failed");
      setSample([]);
      setHeaders([]);
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!file) return setMsg("Choose a file first.");
    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sheet", sheet);
      fd.append("header_row", headerRow);
      fd.append("table", table);
      fd.append("dedupe_key", dedupe);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("replace", replaceAll ? "1" : "0");
      fd.append("merge_components", mergeComponents ? "1" : "0");
      const { data } = await api.post("/import/inventory/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(`Applied: inserted ${data.inserted}, updated ${data.updated}`);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Apply failed");
    } finally {
      setBusy(false);
    }
  };

  const handleMapChange = (targetCol, val) => {
    setMapping((prev) => ({ ...prev, [targetCol]: val }));
  };

  return (
    <div className="container">
      <h2>Inventory Import (Excel)</h2>
      <div className="card" style={{ marginTop: 12, display: "grid", gap: 10 }}>
        <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} />

        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <select className="input" value={table} onChange={(e) => setTable(e.target.value)}>
            {TABLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select className="input" value={sheet} onChange={(e) => setSheet(e.target.value)}>
            <option value="">(use first sheet)</option>
            {sheetNames.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input className="input" style={{ width: 120 }} type="number" min={1} value={headerRow} onChange={(e) => setHeaderRow(e.target.value)} />
          <select className="input" value={dedupe} onChange={(e) => setDedupe(e.target.value)}>
            {DEDUPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button className="btn" onClick={handlePreview} disabled={busy}>Preview</button>
          <button className="btn" onClick={handleApply} disabled={busy || !sample.length}>Apply</button>
          <label className="row" style={{ alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={replaceAll} onChange={(e) => setReplaceAll(e.target.checked)} />
            <span className="muted">Replace table (truncate before import)</span>
          </label>
          <label className="row" style={{ alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={mergeComponents}
              onChange={(e) => setMergeComponents(e.target.checked)}
              disabled={!replaceAll}
            />
            <span className="muted">Merge chassis/body rows by ROWO (only when replace)</span>
          </label>
        </div>

        {msg && <div className="muted">{msg}</div>}

        {headers.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Column Mapping (Target -> Source)</div>
            <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 8 }}>
              {Object.keys(mapping).length === 0 && <div className="muted">No suggested mapping; edit below.</div>}
              {sample.length > 0 && sample[0] && Object.keys(sample[0]).length === 0 ? (
                <div className="muted">No headers parsed.</div>
              ) : null}
              {targets.map((target) => (
                <React.Fragment key={target}>
                  <div className="muted">{target}</div>
                  <input
                    className="input"
                    list="header-options"
                    value={mapping[target] || ""}
                    onChange={(e) => handleMapChange(target, e.target.value)}
                    placeholder="Pick source header"
                  />
                </React.Fragment>
              ))}
            </div>
            <datalist id="header-options">
              {headers.map((h) => (
                <option key={h} value={h} />
              ))}
            </datalist>
          </div>
        )}

        {sample.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Sample Rows (first 10 of {rowCount})</div>
            <div className="card" style={{ maxHeight: 320, overflow: "auto", padding: 8 }}>
              <table className="table">
                <thead>
                  <tr>
                    {headers.map((h) => (
                      <th key={h}>{h || "(blank)"}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sample.map((row, idx) => (
                    <tr key={idx}>
                      {headers.map((h) => (
                        <td key={h}>{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
